import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

const SQUARE_SDK_URLS = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

let squareSdkPromise = null;
export function loadSquareSdk(environment) {
  if (typeof window === 'undefined') return Promise.reject(new Error('Not in browser'));
  if (window.Square) return Promise.resolve(window.Square);
  if (squareSdkPromise) return squareSdkPromise;
  squareSdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SQUARE_SDK_URLS[environment] || SQUARE_SDK_URLS.sandbox;
    s.onload = () => resolve(window.Square);
    s.onerror = () => {
      squareSdkPromise = null;
      reject(new Error('Failed to load Square Web Payments SDK'));
    };
    document.head.appendChild(s);
  });
  return squareSdkPromise;
}

const SquareCardForm = forwardRef(function SquareCardForm({ config, containerStyle, errorStyle, loadingStyle }, ref) {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [err, setErr] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cardInstance = null;
    (async () => {
      try {
        if (!config?.applicationId || !config?.locationId) return;
        const Square = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = Square.payments(config.applicationId, config.locationId);
        cardInstance = await payments.card();
        if (cancelled) return;
        await cardInstance.attach(containerRef.current);
        cardRef.current = cardInstance;
        setReady(true);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
      if (cardInstance) { try { cardInstance.destroy(); } catch {} }
      cardRef.current = null;
    };
  }, [config?.applicationId, config?.locationId, config?.environment]);

  useImperativeHandle(ref, () => ({
    tokenize: async () => {
      if (!cardRef.current) throw new Error('Card form not ready');
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        const detail = (result.errors || []).map(e => e.message).join('; ') || result.status;
        throw new Error(`Card tokenization failed: ${detail}`);
      }
      return result.token;
    },
    ready: () => ready,
  }), [ready]);

  return (
    <div>
      <div ref={containerRef} style={containerStyle || { background: '#fff', padding: 12, borderRadius: 6, minHeight: 56 }} />
      {err && <div style={errorStyle || { color: '#e74c3c', fontSize: '0.85rem', marginTop: 10 }}>{err}</div>}
      {!ready && !err && <div style={loadingStyle || { fontSize: '0.78rem', color: '#999', marginTop: 6 }}>Loading secure card form…</div>}
    </div>
  );
});

export default SquareCardForm;
